/**
 * Seite „Übersicht & Historie" - 1:1-Portierung von `renderOverview` (`index.html:14561-14858`).
 * Klassennamen, Reihenfolge, Texte und Rechenwege sind woertlich uebernommen; ergaenzt wurden
 * ausschliesslich Typen, der Umbau der DB-Bindung auf Props und der Umbau der drei UI-Globals auf
 * `useState`.
 *
 * ================= WICHTIG: es gibt ZWEI Uebersichts-Renderer im Original ==================
 *
 * `index.html` enthaelt daneben `renderOverviewLegacy` (`index.html:14314-14487`) - die alte
 * Fassung mit `.ov-`-Klassen, Tabellen, der Artikelstatistik („Einkaufspreise je Artikel") und der
 * Tabelle „Alle Bestellungen". Sie wird **nirgends aufgerufen**: `renderOverviewLegacy` kommt im
 * ganzen Dokument genau EINMAL vor, naemlich als Definition. Dasselbe gilt fuer ihre alleinigen
 * Zulieferer `articleStats` (14216), `expandedArticle` (4588), `OVERVIEW_FILTER` (4641),
 * `setOverviewFilter` (14257) und `toggleArticle` (14860).
 *
 * Portiert ist deshalb `renderOverview` - die Fassung, die die Anwendung zeigt und gegen die die
 * Baseline `overview-standard` aufgenommen wurde (deren Signatur enthaelt an den Indizes 876-1225
 * genau diesen `ops-`-Baum; kein `.ov-`-Knoten, keine Tabelle). Siehe Task-Bericht, Befund Ü1.
 *
 * ================= Der Umbau: globales DB/CURRENT_USER -> Props =================
 *
 * | Original | hier |
 * |---|---|
 * | `DB.orders` | `bestellungen` |
 * | `DB.suppliers` (ueber `orderCurrency`) | `lieferanten` |
 * | `STATUSES`/`STATUS_MAP`/`STATUS_STAGE` | `definitionen` |
 * | `CURRENT_USER` | `nutzer` |
 * | `isPaymentManager()` | `istZahlungsansicht` |
 * | `openOverviewAction(id, kind)`/`openFromOverview(id)` | `onOeffnen(id, art)` |
 * | `openOverviewTask(id, i)` | `onAufgabeOeffnen(id, i)` (Vorgabe: leitet auf `onOeffnen`) |
 * | `toggleOverviewTask(...)` + `save()` | `onAufgabeUmschalten(id, i, erledigt)` |
 * | `overviewActionType`/`overviewTaskFilter`/`overviewActionLimit` (Modul-`let`) | `useState` |
 *
 * `deals` (`DB.fxDeals`) wird von dieser Seite NICHT gelesen - `fmtMixedTotals` rechnet ueber
 * `orderTotal`, nicht ueber den Effektivkurs. Das Prop steht trotzdem in der Schnittstelle, weil
 * der Task-Brief es vorgibt und die Seite damit dieselbe Form hat wie `KostenSeite`; es ist als
 * `deals?` optional und ausdruecklich ungenutzt.
 *
 * ================= Woertlich uebernommene Eigenheiten =================
 *
 * 1. `active` (= `activeReportingOrders`) und `workflowOrders` sind NICHT dasselbe: die
 *    Zahlungsansicht nimmt auch ARCHIVIERTE Bestellungen mit, sofern sie einen offenen
 *    Zahlungstask haben. Alle KPI-Kacheln ausser „Dringend" und „Offene Tasks" rechnen aber
 *    weiterhin ueber `active` - die Zahlungsansicht zeigt also Zeilen, die in keiner Kachel
 *    auftauchen.
 * 2. `openPayments` filtert `!payment.paid` - das Rohfeld, NICHT `paymentStatusOf(payment)`.
 *    Eine Rate mit `status: 'paid'` ohne `paid: true` zaehlt hier weiter als offen. (Der
 *    Handlungsbedarf nebenan geht ueber `nextDue`, das denselben Rohvergleich benutzt - beide sind
 *    also untereinander stimmig, aber nicht mit `payProgress`.)
 * 3. `arrivingSoon` ruft `daysFromToday(order.eta)` ZWEIMAL je Bestellung auf (`>= 0` und
 *    `<= 14`) - woertlich uebernommen, auch wenn ein Zwischenwert genuegte.
 * 4. Die Sortierung des Handlungsbedarfs vergleicht drei Schluessel: Stufe absteigend, dann
 *    Faelligkeit aufsteigend (`nextDue || eta || '9999-12-31'`), dann Bestelldatum ABSTEIGEND. In
 *    der Zahlungsansicht gilt stattdessen allein `paymentInitiatedAt` absteigend.
 * 5. `heroText` unterscheidet Einzahl/Mehrzahl nur beim VERB („Vorgang braucht"/„Vorgänge
 *    brauchen"), nicht bei „offene Punkte" - „1 offene Punkte sind eingeplant" ist der Originaltext.
 * 6. Der Grenzwert der Aktionsliste schaltet zwischen 6 und `Infinity` (`toggleOverviewActionLimit`,
 *    index.html:14309). Die tote Alt-Fassung prueft dieselbe Variable gegen 10 - ein Vergleich, der
 *    nie zutrifft; hier nicht uebernommen, weil er zur toten Fassung gehoert.
 *
 * ================= Abweichung in der Herstellungsweise (kein Unterschied im Ergebnis) ==========
 *
 * Das Original baut `.ops-grid` mit fuenf Geschwistern und schiebt sie ANSCHLIESSEND per DOM-
 * Operationen in zwei neu erzeugte `.ops-stack`-Huellen (`index.html:14842-14857`). Hier wird die
 * Endform direkt gerendert - dieselbe Knotenfolge, die auch die Baseline zeigt
 * (`div.ops-main-stack.ops-stack` mit Aktions- und Aufgabenpanel, `div.ops-side-stack.ops-stack`
 * mit Insights und Statusverteilung). Ein Nachbau ueber `useEffect`
 * waere in React ein Fremdkoerper und wuerde bei jedem Neuzeichnen gegen die Versoehnung arbeiten.
 *
 * Das Historienpanel „Letzte Aktivitäten", das der Bestand und die Baseline danach zeichnen, gibt es
 * hier nicht mehr: es ist in die eigene Seite „Aktivitäten" umgezogen (`components/AktivitaetenPage/`,
 * Spec 2026-10-18, Teil 1). Der Vergleich gegen die Baseline schneidet es dort aus
 * (`test/komponenten/OverviewPage/uebersichtStruktur.test.tsx`).
 */
import { useMemo, useState } from "react";
import { fmtDate, NUM } from "../../lib/format.ts";
import { LAENDERFILTER_VORGABE } from "../../lib/laender.ts";
import { TRANSPORTARTFILTER_VORGABE } from "../../lib/transportart.ts";
import { fmtMixedTotals } from "../../lib/money.ts";
import { isPaymentRequestTask } from "../../lib/payments.ts";
import { actionKind } from "../../lib/status.ts";
import type { Order, StatusDefinitionen } from "../../lib/status.ts";
import type { Supplier } from "../../lib/types.ts";
import { uebersichtAuswertung } from "../../lib/uebersichtAuswertung.ts";
import type { UebersichtNutzer } from "../../lib/uebersichtAuswertung.ts";
import type { LaenderFilter, TransportartFilter } from "../../state/arbeitsplatz.ts";
import { useAnsicht } from "../../state/useAnsicht.ts";
import { alsAktionsart, alsAufgabenfilter } from "../../state/adresse.ts";
import { AufgabenListe } from "./AufgabenListe.tsx";
import { Einblicke } from "./Einblicke.tsx";
import { Handlungsbedarf } from "./Handlungsbedarf.tsx";
import { Seitenkopf } from "../App/Seitenkopf.tsx";
import { Statusverteilung } from "./Statusverteilung.tsx";
import { Terminaenderungen } from "./Terminaenderungen.tsx";

// `UebersichtNutzer` (nur die beiden Felder, die `renderOverview` von `CURRENT_USER` liest,
// index.html:14596) wohnt seit dem KI-Zugang (Stufe 1) in `lib/uebersichtAuswertung.ts`; der Name bleibt
// hier erreichbar.
export type { UebersichtNutzer };

export interface UebersichtSeiteProps {
  bestellungen: readonly Order[];
  lieferanten: readonly Supplier[] | null | undefined;
  /** `DB.fxDeals` - von dieser Seite ungenutzt, siehe Kopfkommentar. */
  deals?: unknown;
  definitionen: StatusDefinitionen;
  nutzer: UebersichtNutzer | null;
  /** `isPaymentManager()` - der Aufrufer loest die Rolle auf. */
  istZahlungsansicht: boolean;
  onOeffnen: (bestellungId: string, art: string) => void;
  onAufgabeOeffnen?: (bestellungId: string, aufgabenIndex: number) => void;
  onAufgabeUmschalten?: (bestellungId: string, aufgabenIndex: number, erledigt: boolean) => void;
  /**
   * Auftrag F, `Terminaenderungen.tsx`: ob die Uebersicht die aktive Seite ist - reicht unveraendert
   * durch (siehe dortiger Kopfkommentar, "geladen wird erst, wenn die Uebersicht wirklich sichtbar
   * ist"). Default `true`, siehe dort.
   */
  istAktiveSeite?: boolean;
  /**
   * Der EU/China-Laenderfilter (Auftrag „EU/China-Laenderfilter in die App-Kopfzeile verschieben",
   * app-weiter Zustand aus `App.tsx`) - wendet `lib/laender.ts#istBestellungNachLaenderFilterSichtbar`
   * ganz vorn auf `bestellungen` an, bevor irgendeine Kennzahl daraus abgeleitet wird.
   *
   * PFLICHT seit der Pruefrunde 2 (Befund W1) - siehe `Board/BoardSeite.tsx#BoardSeiteProps`,
   * Kopfkommentar am selben Feld, fuer die ausfuehrliche Begruendung.
   */
  laenderFilter: LaenderFilter;
  /**
   * Der Luftfracht/Seefracht-Filter (Auftrag „Luftfracht/Seefracht-Filter im Bestellhub",
   * 2026-08-27, app-weiter Zustand aus `App.tsx`) - wendet
   * `lib/transportart.ts#istBestellungNachTransportartFilterSichtbar` an derselben Stelle wie
   * `laenderFilter` an.
   *
   * PFLICHT seit der Nachbesserung zum Transportart-Filter (nach Commit c9da74e) - siehe `Board/BoardSeite.tsx#BoardSeiteProps`,
   * Kopfkommentar am selben Feld, fuer die ausfuehrliche Begruendung (derselbe Befund wie W1 bei
   * `laenderFilter`, hier auf den Transportart-Filter uebertragen).
   */
  transportartFilter: TransportartFilter;
  /**
   * Kontotyp "Nur lesen" (`state/nurLeser.ts`, einmal in `App.tsx` aus den Rechten berechnet): die Aufgaben
   * lassen sich nicht abhaken (`AufgabenListe`), Terminaenderungen nicht bestaetigen (`Terminaenderungen`).
   * Ohne Angabe: nicht gesperrt.
   */
  nurLeser?: boolean;
}

export function UebersichtSeite({
  bestellungen,
  lieferanten,
  definitionen,
  nutzer,
  istZahlungsansicht,
  onOeffnen,
  onAufgabeOeffnen,
  onAufgabeUmschalten,
  istAktiveSeite,
  laenderFilter = LAENDERFILTER_VORGABE,
  transportartFilter = TRANSPORTARTFILTER_VORGABE,
  nurLeser = false,
}: UebersichtSeiteProps) {
  // index.html:4642-4644 - drei Modul-`let` des Originals. Aktionsart und Aufgabenfilter leben seit
  // Aufgabe 4 in der ADRESSE (Spezifikation § 3.3); `aktionsgrenze` bleibt eine reine Scrollposition
  // und damit ein `useState` (Aufgabe-4-Brief, Nicht-Ziel 3 - NICHT anfassen).
  const { ansicht, setzeAnsicht } = useAnsicht();
  const { aktion: aktionsart, aufgaben: aufgabenfilter } = ansicht.uebersicht;
  // `wert` bleibt hier `string` (die Filterknopf-Props von `Handlungsbedarf`/`AufgabenListe` kennen
  // keinen Aufzaehlungstyp) - `alsAktionsart`/`alsAufgabenfilter` PRUEFEN das an dieser einzigen
  // Naht zur Ansicht, statt es blind zu casten (`as`): ein neuer Filterknopf mit einem noch nicht
  // gelisteten Wert soll hier laut scheitern, nicht erst beim Lesen lautlos wirkungslos werden
  // (Fixrunde 1).
  const setAktionsart = (wert: string): void =>
    setzeAnsicht(
      { uebersicht: { ...ansicht.uebersicht, aktion: alsAktionsart(wert) } },
      { ersetzen: true },
    );
  const setAufgabenfilter = (wert: string): void =>
    setzeAnsicht(
      { uebersicht: { ...ansicht.uebersicht, aufgaben: alsAufgabenfilter(wert) } },
      { ersetzen: true },
    );
  const [aktionsgrenze, setAktionsgrenze] = useState(6);

  // Die Kennzahlen (index.html:14561-14649: Laender-/Transportartfilter ganz vorn, Handlungsbedarf,
  // Aufgaben, Zahlungsfristen, Dokumente, Lieferantenzaehlung, Statusverteilung, Fortschritt) rechnet seit
  // dem KI-Zugang (Stufe 1) `lib/uebersichtAuswertung.ts` - der Server faehrt dieselbe Rechnung. Das
  // `useMemo` und seine Abhaengigkeiten sind unveraendert.
  const daten = useMemo(
    () =>
      uebersichtAuswertung({
        bestellungen,
        lieferanten,
        definitionen,
        istZahlungsansicht,
        nutzer,
        laenderFilter,
        transportartFilter,
      }),
    [bestellungen, definitionen, istZahlungsansicht, nutzer, lieferanten, laenderFilter, transportartFilter],
  );

  // index.html:14570-14571 - in der Zahlungsansicht sind beide Filter festgenagelt.
  const effectiveActionType = daten.paymentManagerView ? "" : aktionsart;
  const effectiveTaskFilter = daten.paymentManagerView ? "open" : aufgabenfilter;

  // index.html:14591-14593
  const filteredActionRows = effectiveActionType
    ? daten.actionRows.filter((row) => actionKind(row.info.labels) === effectiveActionType)
    : daten.actionRows;

  // index.html:14611-14615
  const filteredTasks =
    effectiveTaskFilter === "overdue"
      ? daten.overdueTasks
      : effectiveTaskFilter === "mine"
        ? daten.myTasks
        : daten.openTasks;

  // index.html:14650-14658
  const heroText = daten.paymentManagerView
    ? daten.actionRows.length
      ? `${NUM.format(daten.actionRows.length)} ${daten.actionRows.length === 1 ? "Zahlung kann" : "Zahlungen können"} jetzt bearbeitet werden.`
      : "Aktuell wurde keine Zahlung zur Bearbeitung angestoßen."
    : daten.urgentActions.length
      ? `${NUM.format(daten.urgentActions.length)} dringende ${daten.urgentActions.length === 1 ? "Vorgang braucht" : "Vorgänge brauchen"} heute Aufmerksamkeit.`
      : daten.actionRows.length
        ? `${NUM.format(daten.actionRows.length)} offene Punkte sind eingeplant, aktuell ist nichts kritisch.`
        : "Keine offenen Risiken – der operative Ablauf ist im grünen Bereich.";

  return (
    <div className="ops-shell">
      <Seitenkopf titel="Übersicht" unterzeile={heroText}>
        <div className="ops-date">
          <span>Stand</span>
          <strong>{fmtDate(daten.today)}</strong>
        </div>
      </Seitenkopf>

      <div className="ops-kpis">
        <div className={`ops-kpi ${daten.urgentActions.length ? "urgent" : "good"}`}>
          <div className="ops-kpi-label">Dringend</div>
          <div className="ops-kpi-value">{NUM.format(daten.urgentActions.length)}</div>
          <div className="ops-kpi-note">
            {daten.urgentActions.length ? "sofort priorisieren" : "keine Eskalation"}
          </div>
        </div>
        <div className={`ops-kpi ${daten.overdueTasks.length ? "urgent" : "info"}`}>
          <div className="ops-kpi-label">Offene Tasks</div>
          <div className="ops-kpi-value">{NUM.format(daten.openTasks.length)}</div>
          <div className="ops-kpi-note">{NUM.format(daten.overdueTasks.length)} davon überfällig</div>
        </div>
        <div
          className={`ops-kpi ${
            daten.overduePayments.length ? "urgent" : daten.dueSoonPayments.length ? "warning" : "good"
          }`}
        >
          <div className="ops-kpi-label">Zahlungsfristen</div>
          <div className="ops-kpi-value">
            {NUM.format(daten.overduePayments.length + daten.dueSoonPayments.length)}
          </div>
          <div className="ops-kpi-note">
            {NUM.format(daten.overduePayments.length)} überfällig · {NUM.format(daten.dueSoonPayments.length)} in 7 Tagen
          </div>
        </div>
        <div className="ops-kpi info">
          <div className="ops-kpi-label">Unterwegs</div>
          <div className="ops-kpi-value">{NUM.format(daten.inTransit.length)}</div>
          <div className="ops-kpi-note">
            {fmtMixedTotals(daten.inTransit, lieferanten) || "kein Warenwert"}
          </div>
        </div>
        <div className={`ops-kpi ${daten.arrivingSoon.length ? "warning" : "good"}`}>
          <div className="ops-kpi-label">Ankunft ≤ 14 Tage</div>
          <div className="ops-kpi-value">{NUM.format(daten.arrivingSoon.length)}</div>
          <div className="ops-kpi-note">{NUM.format(daten.missingEta.length)} Bestellungen ohne ETA</div>
        </div>
      </div>

      <div className="ops-grid">
        <div className="ops-stack ops-main-stack">
          <Handlungsbedarf
            zeilen={filteredActionRows}
            zaehler={daten.actionCounts}
            definitionen={definitionen}
            aktionsart={effectiveActionType}
            grenze={aktionsgrenze}
            istZahlungsansicht={daten.paymentManagerView}
            onArtWaehlen={setAktionsart}
            // index.html:14309 - 6 <-> Infinity, kein anderer Wert.
            onGrenzeUmschalten={() => setAktionsgrenze((alt) => (alt === 6 ? Infinity : 6))}
            onOeffnen={onOeffnen}
          />
          {/* Auftrag F: direkt nach Handlungsbedarf - inhaltlich das Naechstverwandte ("jetzt
              ansehen"), siehe Terminaenderungen.tsx-Kopfkommentar. */}
          <Terminaenderungen
            bestellungen={bestellungen}
            onOeffnen={onOeffnen}
            istAktiveSeite={istAktiveSeite}
            nurLeser={nurLeser}
          />
          <AufgabenListe
            zeilen={filteredTasks}
            zaehler={{
              open: daten.openTasks.length,
              overdue: daten.overdueTasks.length,
              mine: daten.myTasks.length,
            }}
            filter={effectiveTaskFilter}
            istZahlungsansicht={daten.paymentManagerView}
            onFilterWaehlen={setAufgabenfilter}
            onAufgabeOeffnen={(id, index) => {
              if (onAufgabeOeffnen) return onAufgabeOeffnen(id, index);
              // index.html:14501 - `openOverviewTask` entscheidet den Reiter am Aufgabentyp.
              const zeile = filteredTasks.find(
                (row) => row.order.id === id && row.taskIndex === index,
              );
              onOeffnen(id, isPaymentRequestTask(zeile?.task) ? "payment" : "task");
            }}
            onAufgabeUmschalten={onAufgabeUmschalten ?? (() => {})}
            nurLeser={nurLeser}
          />
        </div>
        <div className="ops-stack ops-side-stack">
          <Einblicke
            overduePayments={daten.overduePayments.length}
            dueSoonPayments={daten.dueSoonPayments.length}
            missingEta={daten.missingEta.length}
            arrivingSoon={daten.arrivingSoon.length}
            documentDone={daten.documentTotals.done}
            documentTotal={daten.documentTotals.total}
            documentPct={daten.documentPct}
            masterIssues={daten.masterIssues.length}
            topLieferant={daten.topSupplier}
          />
          <Statusverteilung
            zeilen={daten.statusCounts}
            aktiveGesamt={daten.active.length}
            fortschritt={daten.progressPct}
          />
        </div>
      </div>
    </div>
  );
}
